import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { streamChat } from '../../services/chatApi';
import './ChatWidget.css';

const INTENT_LABELS = {
  inventory: 'Inventory',
  ingredients: 'Ingredients',
  production: 'Production',
  finished_goods: 'Finished Goods',
  general: 'General',
};

const TOOL_LABELS = {
  get_stock_levels: 'Checking stock levels…',
  get_low_stock_items: 'Checking low stock alerts…',
  get_reorder_needed: 'Checking reorder points…',
  get_ingredients: 'Fetching ingredient data…',
  get_ingredient_by_name: 'Looking up ingredient…',
  get_cost_summary_by_category: 'Calculating costs…',
  get_products: 'Fetching product list…',
  get_product_recipe: 'Looking up product recipe…',
  get_production_runs: 'Checking production runs…',
  get_production_summary: 'Summarising production…',
  get_active_production_runs: 'Checking active batches…',
  get_finished_goods_inventory: 'Checking finished goods…',
  get_finished_goods_summary: 'Summarising finished goods…',
};

// ── Icons ────────────────────────────────────────────────────
const BotIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden="true">
    <rect x="3" y="8" width="18" height="12" rx="3" />
    <path d="M9 12h.01M15 12h.01" strokeLinecap="round" strokeWidth="2.5" />
    <path d="M9 16h6" strokeLinecap="round" />
    <path d="M12 8V4" strokeLinecap="round" />
    <circle cx="12" cy="3" r="1" fill="currentColor" strokeWidth="0" />
  </svg>
);

const SendIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="M22 2L11 13" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M22 2L15 22l-4-9-9-4 20-7z" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const CloseIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" />
  </svg>
);

const SearchIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <circle cx="11" cy="11" r="8" />
    <path d="M21 21l-4.35-4.35" strokeLinecap="round" />
  </svg>
);

const TrashIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// ── Sub-components ───────────────────────────────────────────
function ThinkingDots() {
  return (
    <div className="chat-thinking-dots" aria-label="Thinking">
      <span /><span /><span />
    </div>
  );
}

function ToolCallPill({ label }) {
  return (
    <div className="chat-tool-pill">
      <SearchIcon />
      <span>{label}</span>
    </div>
  );
}

function MessageBubble({ message }) {
  const isUser = message.role === 'user';
  const showThinking = !isUser && message.thinking && !message.content;
  const showTool = !isUser && message.activeTool;

  return (
    <div className={`chat-message ${isUser ? 'chat-message--user' : 'chat-message--assistant'}`}>
      {!isUser && message.intent && (
        <span className="chat-intent-badge">
          {INTENT_LABELS[message.intent] ?? message.intent}
        </span>
      )}

      {showTool && <ToolCallPill label={TOOL_LABELS[message.activeTool] ?? `Running ${message.activeTool}…`} />}

      <div className={`chat-bubble ${isUser ? '' : 'chat-bubble--markdown'}`}>
        {showThinking
          ? <ThinkingDots />
          : (
            <>
              {isUser
                ? message.content
                : <ReactMarkdown remarkPlugins={[remarkGfm]}>{message.content}</ReactMarkdown>
              }
              {message.streaming && message.content && <span className="chat-cursor" aria-hidden="true" />}
            </>
          )
        }
      </div>
    </div>
  );
}

// ── Main widget ──────────────────────────────────────────────
export default function ChatWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [isAnyModalOpen, setIsAnyModalOpen] = useState(false);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const abortRef = useRef(null);

  useEffect(() => {
    const checkModals = () => {
      const hasModal = !!document.querySelector('.app-drawer-overlay, .modal-overlay, .gsp-dialog-overlay, .timeout-modal-overlay, .session-expired-overlay');
      setIsAnyModalOpen(hasModal);
    };

    checkModals();

    const observer = new MutationObserver(checkModals);
    observer.observe(document.body, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (isOpen) inputRef.current?.focus();
  }, [isOpen]);

  const updateLastAssistant = useCallback((updater) => {
    setMessages((prev) => {
      const next = [...prev];
      const last = next[next.length - 1];
      if (last?.role === 'assistant') {
        next[next.length - 1] = updater(last);
      }
      return next;
    });
  }, []);

  const sendMessage = useCallback(() => {
    const text = input.trim();
    if (!text || isStreaming) return;

    const userMsg = { role: 'user', content: text };
    const assistantMsg = { role: 'assistant', content: '', streaming: true, thinking: true, intent: null, activeTool: null };

    setMessages((prev) => [...prev, userMsg, assistantMsg]);
    setInput('');
    setIsStreaming(true);

    const history = messages.map(({ role, content }) => ({ role, content }));

    abortRef.current = streamChat(text, history, {
      onIntent: (intent) => {
        updateLastAssistant((msg) => ({ ...msg, intent, thinking: true }));
      },

      onToolStart: (tool) => {
        updateLastAssistant((msg) => ({ ...msg, activeTool: tool, thinking: false }));
      },

      onToolEnd: () => {
        updateLastAssistant((msg) => ({ ...msg, activeTool: null, thinking: true }));
      },

      onToken: (token) => {
        setMessages((prev) => {
          const next = [...prev];
          const last = next[next.length - 1];
          if (last?.role === 'assistant') {
            next[next.length - 1] = {
              ...last,
              content: last.content + token,
              thinking: false,
              activeTool: null,
            };
          }
          return next;
        });
      },

      onDone: () => {
        updateLastAssistant((msg) => ({ ...msg, streaming: false, thinking: false, activeTool: null }));
        setIsStreaming(false);
      },

      onError: (err) => {
        console.error('Chat error:', err);
        updateLastAssistant((msg) => ({
          ...msg,
          content: 'Something went wrong. Please try again.',
          streaming: false,
          thinking: false,
          activeTool: null,
        }));
        setIsStreaming(false);
      },
    });
  }, [input, isStreaming, messages, updateLastAssistant]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleClose = () => {
    abortRef.current?.();
    setIsOpen(false);
  };

  const handleClear = () => {
    abortRef.current?.();
    setMessages([]);
    setIsStreaming(false);
    setInput('');
  };

  const statusLine = isStreaming
    ? (messages[messages.length - 1]?.activeTool
        ? (TOOL_LABELS[messages[messages.length - 1].activeTool] ?? 'Fetching data…')
        : 'Thinking…')
    : 'Ask about inventory, production, or ingredients';

  return (
    <>
      <button
        className={`chat-trigger ${isOpen ? 'chat-trigger--active' : ''} ${isAnyModalOpen ? 'chat-trigger--docked' : ''}`}
        onClick={() => setIsOpen((v) => !v)}
        aria-label="Open AI assistant"
        aria-expanded={isOpen}
      >
        <BotIcon />
        {!isOpen && !isAnyModalOpen && <span className="chat-trigger-label">Ask AI</span>}
        {isStreaming && !isOpen && <span className="chat-trigger-pulse" aria-hidden="true" />}
      </button>

      {isOpen && (
        <div className={`chat-panel ${isAnyModalOpen ? 'chat-panel--docked' : ''}`} role="dialog" aria-label="AI assistant">
          <div className="chat-header">
            <div className="chat-header-info">
              <div className={`chat-header-avatar ${isStreaming ? 'chat-header-avatar--active' : ''}`}>
                <BotIcon />
              </div>
              <div>
                <div className="chat-header-title">AI Assistant</div>
                <div className="chat-header-subtitle">{statusLine}</div>
              </div>
            </div>
            <div className="chat-header-actions">
              {messages.length > 0 && !isStreaming && (
                <button className="chat-icon-btn" onClick={handleClear} aria-label="Clear conversation">
                  <TrashIcon />
                </button>
              )}
              <button className="chat-icon-btn" onClick={handleClose} aria-label="Close chat">
                <CloseIcon />
              </button>
            </div>
          </div>

          <div className="chat-messages" aria-live="polite">
            {messages.length === 0 && (
              <div className="chat-empty">
                <BotIcon />
                <p>Hi! Ask me anything about your inventory, ingredients, production runs, or finished goods.</p>
                <div className="chat-suggestions">
                  {['What ingredients are low on stock?', 'How many cans can we produce for Panama Black?', 'What is the cost per can for Panama Black?'].map((s) => (
                    <button key={s} className="chat-suggestion-chip" onClick={() => { setInput(s); inputRef.current?.focus(); }}>
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((msg, i) => (
              <MessageBubble key={i} message={msg} />
            ))}
            <div ref={messagesEndRef} />
          </div>

          <div className="chat-input-area">
            <textarea
              ref={inputRef}
              className="chat-input"
              placeholder="Ask a question…"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              rows={1}
              disabled={isStreaming}
              aria-label="Chat input"
            />
            <button
              className={`chat-send-btn ${isStreaming ? 'chat-send-btn--loading' : ''}`}
              onClick={sendMessage}
              disabled={!input.trim() || isStreaming}
              aria-label="Send message"
            >
              {isStreaming ? <span className="chat-send-spinner" /> : <SendIcon />}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
